<template>
  <div class="app-container">
    <el-alert
      title="展示系统 hotel 表的全部酒店，渠道映射按系统 InnID 和供应商补充展示。可导出选中酒店，或按当前精确筛选条件全量导出。"
      type="info"
      :closable="false"
      show-icon
      class="page-tip"
    />

    <div class="filter-container">
      <el-input
        v-model="listQuery.inn_id"
        class="filter-item"
        clearable
        placeholder="系统 InnID（精确）"
        size="small"
        style="width: 210px"
        @keyup.enter.native="handleFilter"
      />
      <el-select
        v-model="listQuery.target_type"
        class="filter-item"
        clearable
        placeholder="供应商"
        size="small"
        style="width: 150px"
      >
        <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-select
        v-model="listQuery.channel_type"
        class="filter-item"
        clearable
        placeholder="映射渠道"
        size="small"
        style="width: 150px"
      >
        <el-option v-for="item in channelTypes" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-input
        v-model="listQuery.channel_code"
        class="filter-item"
        clearable
        placeholder="渠道酒店ID（精确）"
        size="small"
        style="width: 210px"
        @keyup.enter.native="handleFilter"
      />
      <el-button class="filter-item" type="primary" size="small" @click="handleFilter">查询</el-button>
      <el-button class="filter-item" size="small" @click="resetFilter">重置</el-button>
      <el-button
        class="filter-item"
        type="success"
        size="small"
        :disabled="selectedRows.length === 0"
        :loading="exportLoading && exportMode === 'selected'"
        @click="openExportDialog"
      >导出选中酒店（{{ selectedRows.length }}）</el-button>
      <el-button
        class="filter-item"
        type="warning"
        size="small"
        :loading="exportLoading && exportMode === 'filtered'"
        @click="openFilteredExportDialog"
      >按筛选条件全量导出</el-button>
    </div>

    <el-table
      ref="mappingTable"
      v-loading="listLoading"
      :data="list"
      :row-key="rowKey"
      border
      fit
      highlight-current-row
      size="small"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="50" align="center" :reserve-selection="true" />
      <el-table-column label="系统自增id" width="110" prop="hotel_id">
        <template slot-scope="scope">{{ displayValue(scope.row.hotel_id) }}</template>
      </el-table-column>
      <el-table-column label="系统 InnID" min-width="145" prop="inn_id" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.inn_id) }}</template>
      </el-table-column>
      <el-table-column label="供应商" width="110" prop="target_type">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.target_type" size="mini" effect="plain" :type="supplierTag(scope.row.target_type)">
            {{ supplierName(scope.row.target_type) }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="酒店名称" min-width="190" prop="hotel_name" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.hotel_name) }}</template>
      </el-table-column>
      <el-table-column label="酒店地址" min-width="260" prop="hotel_address" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.hotel_address) }}</template>
      </el-table-column>
      <el-table-column label="锦江映射" min-width="135" prop="jin_jiang_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.jin_jiang_mapping) }}</template>
      </el-table-column>
      <el-table-column label="美团映射" min-width="135" prop="meituan_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.meituan_mapping) }}</template>
      </el-table-column>
      <el-table-column label="携程映射" min-width="135" prop="ctrip_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.ctrip_mapping) }}</template>
      </el-table-column>
      <el-table-column label="艺龙映射" min-width="135" prop="eloon_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.eloon_mapping) }}</template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.limit"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog
      title="选择 Excel 导出字段"
      :visible.sync="exportDialogVisible"
      :close-on-click-modal="!exportLoading"
      width="560px"
    >
      <el-alert :title="exportSummary" type="success" :closable="false" show-icon class="export-tip" />
      <el-checkbox-group v-model="selectedExportFieldKeys" class="export-fields">
        <el-checkbox v-for="field in exportFields" :key="field.key" :label="field.key">
          {{ field.label }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="exportLoading" @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="exportLoading" @click="exportExcel">导出 Excel</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import request from '@/utils/request'
import {
  HOTEL_MAPPING_EXPORT_FIELDS,
  buildHotelMappingSheetRows,
  selectedExportFields,
} from '@/utils/hotelMappingExport'

export default {
  name: 'HotelMappings',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listRequestId: 0,
      listQuery: {
        page: 1,
        limit: 20,
        inn_id: '',
        target_type: '',
        channel_type: '',
        channel_code: '',
      },
      suppliers: [
        { label: '锦江', value: 'jin_jiang' },
        { label: '龙腾', value: 'long_teng' },
        { label: '隧穿亚朵', value: 'sui_chuan' },
        { label: '丽呈', value: 'li_cheng' },
      ],
      channelTypes: [
        { label: '锦江', value: 'jin_jiang' },
        { label: '美团', value: 'meituan' },
        { label: '携程', value: 'ctrip' },
        { label: '艺龙', value: 'eloon' },
      ],
      selectedRows: [],
      exportDialogVisible: false,
      exportFields: HOTEL_MAPPING_EXPORT_FIELDS,
      exportMode: 'selected',
      exportLoading: false,
      exportLoadedCount: 0,
      selectedExportFieldKeys: HOTEL_MAPPING_EXPORT_FIELDS.map(field => field.key),
    }
  },
  computed: {
    exportSummary() {
      if (this.exportMode === 'selected') {
        return '将导出已选中的 ' + this.selectedRows.length + ' 家酒店'
      }
      if (this.exportLoading) {
        return '正在按筛选条件读取数据，已加载 ' + this.exportLoadedCount + ' 家酒店'
      }
      return '将按当前筛选条件全量导出，共 ' + this.total + ' 家酒店'
    },
  },
  created() {
    this.getList()
  },
  beforeDestroy() {
    this.listRequestId += 1
  },
  methods: {
    getList() {
      const requestId = ++this.listRequestId
      this.listLoading = true
      return request({
        url: '/api/backend/hotel/hotel-mappings',
        method: 'get',
        params: { ...this.listQuery },
      }).then((response) => {
        if (requestId !== this.listRequestId) return
        this.list = response.data.items
        this.total = response.data.total
      }).finally(() => {
        if (requestId === this.listRequestId) this.listLoading = false
      })
    },
    handleFilter() {
      this.clearSelection()
      this.listQuery.page = 1
      this.getList()
    },
    resetFilter() {
      const limit = this.listQuery.limit
      this.clearSelection()
      this.listQuery = {
        page: 1,
        limit,
        inn_id: '',
        target_type: '',
        channel_type: '',
        channel_code: '',
      }
      this.getList()
    },
    handleSizeChange(value) {
      this.listQuery.limit = value
      this.listQuery.page = 1
      this.getList()
    },
    handleCurrentChange(value) {
      this.listQuery.page = value
      this.getList()
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows
    },
    clearSelection() {
      this.selectedRows = []
      if (this.$refs.mappingTable) this.$refs.mappingTable.clearSelection()
    },
    rowKey(row) {
      return row.hotel_id
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    supplierName(value) {
      const supplier = this.suppliers.find(item => item.value === value)
      return supplier ? supplier.label : this.displayValue(value)
    },
    supplierTag(value) {
      const tags = { jin_jiang: 'success', long_teng: 'warning', sui_chuan: 'info', li_cheng: '' }
      return Object.prototype.hasOwnProperty.call(tags, value) ? tags[value] : 'info'
    },
    openExportDialog() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先选择要导出的酒店')
        return
      }
      this.exportMode = 'selected'
      this.exportLoadedCount = 0
      this.exportDialogVisible = true
    },
    openFilteredExportDialog() {
      this.exportMode = 'filtered'
      this.exportLoadedCount = 0
      this.exportDialogVisible = true
    },
    async exportExcel() {
      if (this.exportMode === 'selected' && !this.selectedRows.length) {
        this.$message.warning('请先选择要导出的酒店')
        return
      }
      const fields = selectedExportFields(this.selectedExportFieldKeys)
      if (!fields.length) {
        this.$message.warning('请至少选择一个导出字段')
        return
      }

      this.exportLoading = true
      this.exportLoadedCount = 0
      try {
        const rows = this.exportMode === 'selected' ? this.selectedRows : await this.fetchAllFilteredRows()
        if (!rows.length) {
          this.$message.warning('当前筛选条件没有可导出的酒店')
          return
        }
        this.writeExcel(rows, fields)
        this.exportDialogVisible = false
        this.$message.success('已导出 ' + rows.length + ' 家酒店')
      } catch (error) {
        this.$message.error('全量导出失败，请稍后重试')
      } finally {
        this.exportLoading = false
      }
    },
    async fetchAllFilteredRows() {
      const rows = []
      const filters = {
        inn_id: this.listQuery.inn_id,
        target_type: this.listQuery.target_type,
        channel_type: this.listQuery.channel_type,
        channel_code: this.listQuery.channel_code,
      }
      let afterId = null
      let hasMore = false
      do {
        const response = await request({
          url: '/api/backend/hotel/hotel-mappings/export',
          method: 'get',
          params: { ...filters, limit: 500, after_id: afterId || undefined },
        })
        const batch = response.data.items || []
        rows.push(...batch)
        this.exportLoadedCount = rows.length
        hasMore = Boolean(response.data.has_more)
        const nextAfterId = response.data.next_after_id
        if (hasMore && (!nextAfterId || String(nextAfterId) === String(afterId))) {
          throw new Error('Invalid export cursor')
        }
        afterId = nextAfterId
      } while (hasMore)
      return rows
    },
    writeExcel(rows, fields) {
      const sheet = XLSX.utils.aoa_to_sheet(
        buildHotelMappingSheetRows(rows, this.selectedExportFieldKeys, this.supplierName),
      )
      sheet['!cols'] = fields.map(field => ({ wch: field.width }))
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, sheet, '酒店映射')
      XLSX.writeFile(workbook, '酒店映射_' + this.timestamp() + '.xlsx')
    },
    timestamp() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      return now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '_' +
        pad(now.getHours()) + pad(now.getMinutes()) + pad(now.getSeconds())
    },
  },
}
</script>

<style scoped>
.page-tip { margin-bottom: 16px; }
.filter-container { margin-bottom: 12px; }
.filter-item { margin-right: 8px; margin-bottom: 8px; }
.pagination-container { padding: 20px 0; text-align: right; }
.export-tip { margin-bottom: 18px; }
.export-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}
.export-fields ::v-deep .el-checkbox { margin-right: 0; }
</style>
